/**
 * Lifecycle query-key family.
 *
 * Inspection / observation / finding / rectification data is read under SEVERAL
 * different query-key namespaces across pages (e.g. an observation is keyed
 * "observations" on one screen, "negative-observations" on another, "observation"
 * on its detail, and "inspection-noms" inside the questions page). A single
 * mutation typically affects more than one of these, so hand-listing keys in
 * each mutation's onSuccess drifts and leaves stale screens.
 *
 * Instead, a global MutationCache.onSuccess (see App.tsx) invalidates this whole
 * family by first-segment prefix after ANY successful mutation. Active queries
 * refetch immediately (fresh from the server via readThrough); inactive ones are
 * marked stale and refetch the next time their page mounts — so every screen
 * reflects a change without a manual refresh.
 */
import type { Query } from "@tanstack/react-query";

export const LIFECYCLE_QUERY_KEYS: ReadonlySet<string> = new Set([
  // Inspections + their derived/aggregate views
  "inspections", "inspection", "inspection-summary", "inspection-answers",
  "inspection-questions", "inspection-noms", "inspection-findings",
  "inspection-answer-detail", "answer-detail-for-attachments",
  "vessel-dashboard", "company-admin", "reports-list", "risk-heat-map",
  "cycle-comparison",
  // Observations
  "observations", "negative-observations", "observation",
  // Findings
  "findings", "deficiency-findings", "deficiency-finding",
  // Rectifications + root causes
  "rectifications", "root-causes", "root-cause-categories",
  // Attachments that hang off the above
  "attachments", "response-attachments", "aspect-attachments",
  "photo-comparison-attachments",
  // Notifications reflect lifecycle events (submit / send-back / accept …)
  "notifications", "notifications-unread",
]);

/** True when a query reads inspection-lifecycle data (matched by key prefix). */
export function isLifecycleQuery(query: Query): boolean {
  const head = query.queryKey[0];
  return typeof head === "string" && LIFECYCLE_QUERY_KEYS.has(head);
}
